/**
 * `defaultTheme.colors.primary` de frontend-app (`src/theme/defaultTheme.ts`).
 * Misma base que usa `mergeTenantTheme` cuando el tenant no trae primary usable.
 */
export const PLATFORM_DEFAULT_BRAND_COLOR = "#2e7d32";

// tenant.theme es una columna de texto libre: hay themes con JSON válido y otros con
// claves sin comillas ({primary:"blue"}). La reparación replica la que ya hace la tabla
// de tenants (pages/Tenants/components/TableTemplates.tsx).
const parseTheme = (theme: string): Record<string, unknown> | null => {
  const attempt = (value: string): Record<string, unknown> | null => {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === "object" && parsed !== null
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  };

  return (
    attempt(theme) ??
    attempt(
      theme
        .replace(/([{,]\s*)(\w+)(\s*:)/g, '$1"$2"$3')
        .replace(/:\s*'([^']+)'/g, ': "$1"')
    )
  );
};

const toHexChannel = (channel: number): string =>
  Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, "0");

const rgbToHex = (value: string): string | null => {
  const rgb = value.match(
    /^rgba?\(\s*([\d.]+)\s*[,\s]\s*([\d.]+)\s*[,\s]\s*([\d.]+)\s*(?:[,/]\s*[\d.%]+\s*)?\)$/
  );
  if (!rgb) {
    return null;
  }
  return `#${toHexChannel(Number(rgb[1]))}${toHexChannel(Number(rgb[2]))}${toHexChannel(
    Number(rgb[3])
  )}`;
};

const hexLiteralToHex = (value: string): string | null => {
  if (/^#[0-9a-f]{6}$/.test(value)) {
    return value;
  }
  if (/^#[0-9a-f]{8}$/.test(value)) {
    return value.slice(0, 7);
  }
  if (/^#[0-9a-f]{3,4}$/.test(value)) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }
  return null;
};

/**
 * Los demás formatos se resuelven delegándolos al parser del navegador, en vez de
 * mantener a mano una lista de nombres de color: así valen los nombres CSS y hsl() sin
 * tocar este archivo, y un valor inválido devuelve null en lugar de romper. Los
 * formatos que el navegador reporta sin convertir a rgb (oklch, lab) caen a null.
 */
const cssColorToHex = (value: string): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const probe = document.createElement("span");
  probe.style.display = "none";
  probe.style.color = value;
  if (!probe.style.color) {
    return null;
  }

  document.body.appendChild(probe);
  const computed = getComputedStyle(probe).color;
  probe.remove();

  return rgbToHex(computed);
};

const normalizeColor = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const color = value.trim().toLowerCase();
  if (!color) {
    return null;
  }
  return hexLiteralToHex(color) ?? rgbToHex(color) ?? cssColorToHex(color);
};

/**
 * Mapa de colores del theme: anidado bajo `colors` cuando el tenant guardó un Theme
 * completo, o el objeto mismo cuando guardó solo los colores. Es la misma decisión que
 * toma mergeTenantTheme en frontend-app.
 */
const themeColors = (parsed: Record<string, unknown>): Record<string, unknown> => {
  const nested = parsed.colors;
  return nested && typeof nested === "object"
    ? (nested as Record<string, unknown>)
    : parsed;
};

export const getTenantPrimaryColor = (theme?: string | null): string => {
  if (!theme || theme === "{}") {
    return PLATFORM_DEFAULT_BRAND_COLOR;
  }

  const parsed = parseTheme(theme);
  if (!parsed) {
    return PLATFORM_DEFAULT_BRAND_COLOR;
  }

  return normalizeColor(themeColors(parsed).primary) ?? PLATFORM_DEFAULT_BRAND_COLOR;
};

/** Los charts conservan el fallback de marca para primary blanco o negro. */
export const getTenantBrandColor = (theme?: string | null): string => {
  const color = getTenantPrimaryColor(theme);
  if (color === "#ffffff" || color === "#000000") {
    return PLATFORM_DEFAULT_BRAND_COLOR;
  }
  return color;
};
